import React, { useState } from 'react';
import { useGetAllOrdersQuery, useDeleteOrderByIdMutation, useUpdateOrderMutation } from '../../../store/api/order.api';
import styles from './OrderAdminPage.module.css';
import { OrderModal } from '../Modal/OrderModal/OrderModal';
import { MessageModal } from '../Modal/MessageModal/MessageModal';
import { useNavigate } from 'react-router-dom';

export const OrderAdminPage = () => {
	const schema = window.sessionStorage.getItem('schema');

	const { data: orders = [], isLoading, refetch } = useGetAllOrdersQuery({schema});
	const [activeMenu, setActiveMenu] = useState(null);
	const [editOrderId, setEditOrderId] = useState(null);
	const [createMode, setCreateMode] = useState(false);
	const [viewOrderId, setViewOrderId] = useState(null);
	const [statusFilter, setStatusFilter] = useState('all');
	const [userIdSearch, setUserIdSearch] = useState(''); // 👈 поиск по userId
	const [currentPage, setCurrentPage] = useState(1);
	const [itemsPerPage] = useState(10);
	const [messageData, setMessageData] = useState({
		orderId: null,
		userId: null,
	});
	const navigate = useNavigate();

	const [deleteOrder] = useDeleteOrderByIdMutation();
	const [updateOrder] = useUpdateOrderMutation();

	const handleMenuToggle = (orderId) => {
		setActiveMenu((prev) => (prev === orderId ? null : orderId));
	};

	const handleSendMessage = (orderId, userId) => {
		setMessageData({ orderId, userId });
		setActiveMenu(null);
	};

	const handleCloseMessageModal = () => {
		setMessageData({ orderId: null, userId: null });
	};

	const handleEdit = (orderId) => {
		setEditOrderId(orderId);
		setActiveMenu(null);
	};

	const handleViewDetails = (orderId) => {
		setViewOrderId(orderId);
		setActiveMenu(null);
	};

	const handleCloseModal = () => {
		setEditOrderId(null);
		setViewOrderId(null);
		setCreateMode(false);
	};

	const handleDelete = async (orderId) => {
		if (window.confirm('Вы уверены, что хотите удалить этот заказ?')) {
			try {
				await deleteOrder({id: orderId, schema}).unwrap();
				refetch();
			} catch (error) {
				alert('Ошибка при удалении заказа');
			}
		}
	};

	// Сортировка + фильтрация заказов по статусу и userId
	const filteredOrders = orders
		.slice()
		.sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt))
		.filter((order) => {
			const statusMatches = statusFilter === 'all' || order.status === statusFilter;
			const userIdMatches = userIdSearch === '' || order.userId?.toString().includes(userIdSearch);
			return statusMatches && userIdMatches;
		});

	// Определяем, какие заказы показывать на текущей странице
	const indexOfLastOrder = currentPage * itemsPerPage;
	const indexOfFirstOrder = indexOfLastOrder - itemsPerPage;
	const currentOrders = filteredOrders.slice(indexOfFirstOrder, indexOfLastOrder);

	// Рассчитываем количество страниц
	const totalPages = Math.ceil(filteredOrders.length / itemsPerPage);

	// Переключение страниц
	const handlePageChange = (newPage) => {
		if (newPage >= 1 && newPage <= totalPages) {
			setCurrentPage(newPage);
		}
	};

	if (isLoading) {
		return <div className={styles.loading}>Загрузка...</div>;
	}

	return (
		<div className={styles.main}>
			<button onClick={() => navigate(-1)} className={styles.backButton}>
				← Назад
			</button>
			<h2 className={styles.title}>Заказы</h2>

			<div className={styles.controls}>
				{/* Фильтр по статусу */}
				<div className={styles.filterWrapper}>
					<select
						value={statusFilter}
						onChange={(e) => {
							setStatusFilter(e.target.value);
							setCurrentPage(1);
						}}
						className={styles.filterSelect}
					>
						<option value="all">Все статусы</option>
						<option value="Оплачен">Оплачен</option>
						<option value="Отменен">Отменен</option>
						<option value="Создан">Создан</option>
					</select>
				</div>

				{/* Поиск по userId */}
				<div className={styles.searchWrapper}>
					<input
						type="text"
						placeholder="Поиск по ID пользователя"
						value={userIdSearch}
						onChange={(e) => {
							setUserIdSearch(e.target.value);
							setCurrentPage(1); // при изменении поиска возвращаемся на первую страницу
						}}
						className={styles.searchInput}
					/>
				</div>
			</div>

			<div className={styles.header}>
				<div>ID/номер</div>
				<div>Цена</div>
				<div>Статус</div>
				<div>Время заказа</div>
				<div>Пользователь ID</div>
				<div>Действия</div>
			</div>

			<div className={styles.list}>
				{currentOrders.map((order) => (
					<div key={order.id} className={styles.row}>
						<div>{order.id}</div>
						<div>{order.price}</div>
						<div>{order.status}</div>
						<div>{new Date(order.createdAt).toLocaleString()}</div>
						<div>{order.userId}</div>
						<div className={styles.actions}>
							<div onClick={() => handleMenuToggle(order.id)} className={styles.menuIcon}>
								⋮
							</div>
							{activeMenu === order.id && (
								<div className={styles.menu}>
									<div onClick={() => handleEdit(order.id)}>Изменить</div>
									<div onClick={() => handleDelete(order.id)}>Удалить</div>
									<div onClick={() => handleViewDetails(order.id)}>Подробнее</div>
									<div onClick={() => handleSendMessage(order.id, order.userId)}>Отправить сообщение</div>
								</div>
							)}
						</div>
					</div>
				))}
			</div>

			{/* Пагинация */}
			<div className={styles.pagination}>
				<button
					onClick={() => handlePageChange(currentPage - 1)}
					disabled={currentPage === 1}
					className={styles.pageButton}
				>
					Назад
				</button>
				<span className={styles.pageInfo}>
					Страница {currentPage} из {totalPages}
				</span>
				<button
					onClick={() => handlePageChange(currentPage + 1)}
					disabled={currentPage === totalPages}
					className={styles.pageButton}
				>
					Вперёд
				</button>
			</div>

			{/* Модальное окно для редактирования, создания или просмотра заказа */}
			{(editOrderId || createMode || viewOrderId) && (
				<OrderModal
					isOpen={editOrderId !== null || createMode || viewOrderId !== null}
					onClose={handleCloseModal}
					onSubmit={async (formData) => {
						try {
							if (editOrderId) {
								await updateOrder({ id: editOrderId, data: formData, schema }).unwrap();
								alert('Заказ обновлен');
							} else if (createMode) {
								alert('Новый заказ создан');
							}
							refetch();
							handleCloseModal();
						} catch (error) {
							alert('Ошибка при обновлении заказа');
						}
					}}
					order={viewOrderId ? orders.find((order) => order.id === viewOrderId) : (createMode ? null : orders.find((order) => order.id === editOrderId))}
					viewMode={!!viewOrderId}
					schema={schema}
				/>
			)}

			{messageData.orderId && (
				<MessageModal
					isOpen={!!messageData.orderId}
					onClose={handleCloseMessageModal}
					orderId={messageData.orderId}
					userId={messageData.userId}
					schema={schema}
				/>
			)}
		</div>
	);
};
